import { normalizeResult } from "../../helpers/copilotResult";
import { normalizeInsights } from "../../helpers/copilotInsights";
import * as endPoints from "../../constants/v2/endPoints";
import * as apiService from "./apiService";

// REAL CONTRACT, reconciled against Gitcha-API #609
// (src/controllers/v2/chatHistory.controller.js /
// src/models/Chat_Conversations.model.js / Chat_Messages.model.js):
//
//   GET /api/v2/help/chat/conversations
//     -> { errorCode: 0, result: [
//          { Id, ConversationUid, Title, CreatedOn, LastUpdatedTS }, ...
//        ] }
//
//   GET /api/v2/help/chat/conversations/:conversationId/messages
//     (":conversationId" in the URL is the client-facing ConversationUid,
//     not the internal numeric Id - the API's own route comment confirms
//     this, and it's what listChatConversations already hands back below)
//     -> { errorCode: 0, result: {
//          conversation: { Id, ConversationUid, Title, ... },
//          messages: [{ Id, MessageUid, Role, Content, CreatedOn }, ...],
//        } }
//
// The API's field names (PascalCase, ConversationUid/Role/Content) differ
// from what the rest of the FE (ChatPage, the chatHistory reducer,
// HelpChatPanel) consumes (conversationId/title/role/content) - adaptXxx
// below translate at this one boundary so nothing upstream has to know
// about the API's raw shape, and so a future API field rename only needs a
// change here.
//
// Both go through the token-aware apiCall (same as createSupportTicket),
// since chat history is per-user.

// A 404 here means the requested conversation/list genuinely doesn't exist
// for this user yet (e.g. a brand-new account with no chat history) - not
// "the endpoint isn't deployed," now that #609 is live. Still worth
// degrading gracefully rather than throwing, since "no history yet" is a
// normal, expected state for the sidebar to render as empty.
const NOT_FOUND_STATUS = 404;

const adaptConversationListItem = (row) => ({
  conversationId: row?.ConversationUid,
  title: row?.Title,
  updatedAt: row?.LastUpdatedTS,
});

// ResultCard arrives already parsed from a json column, but a driver or
// proxy that hands it back as text should not cost the agent the card - the
// whole reason it is stored is that losing it is the bug.
//
// Then through normalizeResult, the same function the live `result` SSE
// event goes through, for two reasons. Both paths must produce one shape, or
// a rehydrated card renders differently from the one the agent just watched
// appear. And normalizeResult is where an action's href is checked against
// the route allowlist: a stored card is data coming back over the wire like
// any other, so it earns no more trust than the stream did.
const parseCard = (raw, normalize) => {
  if (!raw) return undefined;
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!parsed || typeof parsed !== "object") return undefined;
  return normalize(parsed) || undefined;
};

const parseResultCard = (raw) => parseCard(raw, normalizeResult);

// Same idea and the same reasoning as parseResultCard above, for
// gitcha-mcp's `insights` event (InsightsCard) - independent column, and
// independently undefined/present, since a turn can carry either, both, or
// neither.
const parseInsightsCard = (raw) => parseCard(raw, normalizeInsights);

const adaptMessage = (row) => ({
  role: row?.Role,
  content: row?.Content,
  serverMessageId: row?.MessageUid,
  result: parseResultCard(row?.ResultCard),
  insights: parseInsightsCard(row?.InsightsCard),
});

export async function listChatConversations() {
  try {
    const response = await apiService.apiCall(
      endPoints.CHAT_CONVERSATIONS_LIST,
      "GET",
    );
    return {
      ...response,
      result: (response?.result || []).map(adaptConversationListItem),
    };
  } catch (err) {
    if (err?.response?.status === NOT_FOUND_STATUS) {
      return { errorCode: 0, result: [] };
    }
    throw err;
  }
}

// A 404 here means the conversation is already gone, or was never this
// user's - the API scopes the DELETE by UserId. Either way the caller's intent
// is satisfied, so treat it as success rather than surfacing an error for a
// row that no longer exists.
export async function deleteChatConversation(conversationId) {
  try {
    return await apiService.apiCall(
      endPoints.CHAT_CONVERSATION_DELETE(conversationId),
      "DELETE",
    );
  } catch (err) {
    if (err?.response?.status === NOT_FOUND_STATUS) {
      return { errorCode: 0 };
    }
    throw err;
  }
}

export async function getChatConversation(conversationId) {
  try {
    const response = await apiService.apiCall(
      endPoints.CHAT_CONVERSATION_DETAIL(conversationId),
      "GET",
    );
    const conversation = response?.result?.conversation;
    const messages = response?.result?.messages;
    return {
      ...response,
      result: conversation
        ? {
            conversationId: conversation.ConversationUid,
            title: conversation.Title,
            messages: (messages || []).map(adaptMessage),
          }
        : null,
    };
  } catch (err) {
    if (err?.response?.status === NOT_FOUND_STATUS) {
      return { errorCode: 0, result: null };
    }
    throw err;
  }
}
